import React from 'react';

import { useAccountHistoricalPerformance, useBenchmarkComparison } from 'views/portfolio/reporting-queries';

import { useReportingBenchmarks } from '../reporting/useReportingBenchmarks';
import { useReportingScope } from '../reporting/useReportingScope';
import { benchmarkColor, buildBenchmarkOverlays, toBenchmarkValueMaps } from './benchmark-overlays';
import { AdvancedAnalytics } from './components/AdvancedAnalytics';
import { NavChart, NavChartOverlay } from './components/NavChart';
import { PerformanceBreakdownTable } from './components/PerformanceBreakdownTable';
import { NAV_CHART_NET_INVESTED_LINE_COLOR } from './constants';

const NET_INVESTED_OVERLAY: NavChartOverlay = {
    key: 'netInvestedCapital',
    source: 'performance',
    field: 'netInvestedCapital',
    label: 'Net invested capital',
    color: NAV_CHART_NET_INVESTED_LINE_COLOR,
};

// The Performance section of the reporting overview: the market value chart with net invested
// capital and the header's benchmarks, performance by portfolio and the benchmark statistics.
// Reads its scope from the URL like the other sections.
export const PerformanceSection = () => {
    const { householdGUID, dateRange, selectedAccountGUIDs, includeClosed } = useReportingScope();
    const { benchmarkNames, availableBenchmarks } = useReportingBenchmarks();
    const { data: history, isPending: isHistoryPending } = useAccountHistoricalPerformance({
        householdGUID,
        fromInclusive: dateRange.fromInclusive,
        toInclusive: dateRange.toInclusive,
        groupByGuids: selectedAccountGUIDs,
        includeClosed,
    });

    const { data: benchmarkSeries, isFetching: isBenchmarkFetching } = useBenchmarkComparison({
        householdGUID,
        fromInclusive: dateRange.fromInclusive,
        toInclusive: dateRange.toInclusive,
        groupByGuids: selectedAccountGUIDs,
        includeClosed,
        benchmarkNames,
    });

    const overlays = [
        NET_INVESTED_OVERLAY,
        ...buildBenchmarkOverlays({
            benchmarkNames,
            availableBenchmarks,
            valuesByBenchmark: toBenchmarkValueMaps(benchmarkSeries ?? []),
            isFetching: isBenchmarkFetching,
        }),
    ];
    // Advanced Analytics needs a benchmark to compare against: with none picked it falls back to the
    // first available one, as it did before the benchmark moved to the header.
    const statisticsBenchmarks = benchmarkNames.length > 0 ? benchmarkNames : availableBenchmarks.slice(0, 1);
    const benchmarks = statisticsBenchmarks.map((name) => ({ name, color: benchmarkColor(name, availableBenchmarks) }));

    return (
        <>
            <NavChart data={history} isLoading={isHistoryPending} overlays={overlays} />
            <PerformanceBreakdownTable
                householdGUID={householdGUID}
                fromInclusive={dateRange.fromInclusive}
                toInclusive={dateRange.toInclusive}
                includeClosed={includeClosed}
            />
            <AdvancedAnalytics
                householdGUID={householdGUID}
                selectedAccountGUIDs={selectedAccountGUIDs}
                includeClosed={includeClosed}
                benchmarks={benchmarks}
            />
        </>
    );
};
